import { HttpClient } from '@angular/common/http';
import { EventEmitter, Inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { MailBatchDetail } from '../Models/MailBatchDetail';

@Injectable({
	providedIn: 'root'
})
export class MailService {
	public loading: boolean = false;
	public mailBatchChangedEvent: EventEmitter<number[]> = new EventEmitter<number[]>();
	public refreshMailBatches = new EventEmitter<boolean>();
	public newMailBatchesNotificationEvent: EventEmitter<number[]> = new EventEmitter<number[]>();

	constructor(
		private httpClient: HttpClient,
		@Inject('environment') private environment: any
	) {}

	public getMailOutboxes(): Observable<any[]> {
		return this.httpClient.get<any[]>(this.environment.apiUrl + 'api/Contract/GetMailOutboxes');
	}

	public getMailOutboxCounts(): Observable<any[]> {
		return this.httpClient.get<any[]>(this.environment.apiUrl + 'api/Contract/GetMailOutboxCounts');
	}

	public getReturnMailReasons(): Observable<any[]> {
		return this.httpClient.get<any[]>(this.environment.apiUrl + 'api/Contract/GetReturnMailReasons');
	}

	public getStates(): Observable<any[]> {
		return this.httpClient.get<any[]>(this.environment.apiUrl + 'api/Contract/GetStates');
	}

	public getComplianceDateChangeReasons(): Observable<any[]> {
		return this.httpClient.get<any[]>(this.environment.apiUrl + 'api/Contract/GetComplianceDateChangeReasons');
	}

	public markMailBatchesApproved(mailBatchIds: number[]): Promise<any> {
		return this.httpClient
			.post<any>(this.environment.apiUrl + 'api/Contract/MarkMailBatchesApproved', mailBatchIds)
			.toPromise()
			.then(() => {
				this.mailBatchChangedEvent.emit(mailBatchIds);
			});
	}

	public markMailBatchesPrinted(mailBatchIds: number[]): Promise<any> {
		return this.httpClient
			.post<any>(this.environment.apiUrl + 'api/Contract/MarkMailBatchesPrinted', mailBatchIds)
			.toPromise()
			.then(() => {
				this.mailBatchChangedEvent.emit(mailBatchIds);
			});
	}

	public markMailBatchesSent(mailBatchIds: number[]): Promise<any> {
		return this.httpClient
			.post<any>(this.environment.apiUrl + 'api/Contract/MarkMailBatchesSent', mailBatchIds)
			.toPromise()
			.then(() => {
				this.mailBatchChangedEvent.emit(mailBatchIds);
			});
	}

	public markMailBatchesSubcontracted(mailBatchIds: number[]): Promise<any> {
		return this.httpClient
			.post<any>(this.environment.apiUrl + 'api/Contract/MarkMailBatchesSubcontracted', mailBatchIds)
			.toPromise()
			.then(() => {
				this.mailBatchChangedEvent.emit(mailBatchIds);
			});
	}

	public unprepareMailBatches(mailBatchIds: number[], unprepareReasonId: number): Promise<any> {
		return this.httpClient
			.post<any>(this.environment.apiUrl + 'api/Contract/UnprepareMailBatches', {
				mailBatchIds: mailBatchIds,
				unprepareReasonId: unprepareReasonId
			})
			.toPromise()
			.then(() => {
				this.mailBatchChangedEvent.emit(mailBatchIds);
			});
	}

	public returnMail_GetLetterDetails(letterId: number): Promise<any> {
		return this.httpClient
			.get<any>(this.environment.apiUrl + 'api/Contract/ReturnMail_GetLetterDetails?letterId=' + letterId)
			.toPromise();
	}

	public returnMail_ProcessReturn(
		letterId: number,
		returnedDate: Date,
		postmarkedDate: Date,
		returnMailReasonId: number,
		resendTo_Name: string,
		resendTo_AddressLine1: string,
		resendTo_AddressLine2: string,
		resendTo_City: string,
		resendTo_StateId: number,
		resendTo_Zip: string
	): Promise<any> {
		return this.httpClient
			.post<any>(this.environment.apiUrl + 'api/Contract/ReturnMail_ProcessReturn', {
				letterId: letterId,
				returnedDate: returnedDate,
				postmarkedDate: postmarkedDate,
				returnMailReasonId: returnMailReasonId,
				resendTo_Name: resendTo_Name,
				resendTo_AddressLine1: resendTo_AddressLine1,
				resendTo_AddressLine2: resendTo_AddressLine2,
				resendTo_City: resendTo_City,
				resendTo_StateId: resendTo_StateId,
				resendTo_Zip: resendTo_Zip
			})
			.toPromise();
	}

	public returnMail_ProcessReturns(
		letterIds: number[],
		returnedDate: Date,
		postmarkedDate: Date,
		returnMailReasonId: number
	): Promise<any> {
		return this.httpClient
			.post<any>(this.environment.apiUrl + 'api/Contract/ReturnMail_ProcessReturns', {
				letterIds: letterIds,
				returnedDate: returnedDate,
				postmarkedDate: postmarkedDate,
				returnMailReasonId: returnMailReasonId
			})
			.toPromise();
	}

	public returnMail_ValidateBarcodes(barcodes: string[]): Promise<any> {
		return this.httpClient
			.post<any>(this.environment.apiUrl + 'api/Contract/ReturnMail_ValidateBarcodes', barcodes)
			.toPromise();
	}

	public downloadMailFileById(mailFileId: number): Promise<any> {
		return this.httpClient
			.get(this.environment.apiUrl + `api/Contract/DownloadMailFileById?mailFileId=${mailFileId}`, {
				responseType: 'arraybuffer'
			})
			.toPromise();
	}

	public downloadMailFileByIdSave(mailFileId: number): Promise<any> {
		this.loading = true;
		return this.httpClient
			.get(this.environment.apiUrl + `api/Contract/DownloadMailFileById?mailFileId=${mailFileId}`, {
				responseType: 'blob'
			})
			.toPromise()
			.then((result) => {
				const blob = new Blob([result], { type: 'application/pdf' });
				const url = window.URL.createObjectURL(blob);
				window.open(url);
			})
			.finally(() => (this.loading = false));
	}

	public downloadMailFileById_SaveAs(mailFileId: number): Promise<any> {
		this.loading = true;
		return this.httpClient
			.get(this.environment.apiUrl + `api/Contract/DownloadMailFileById_AsAttachment?mailFileId=${mailFileId}`, {
				responseType: 'blob',
				observe: 'response'
			})
			.toPromise()
			.then((response) => {
				const contDisp = response.headers.get('Content-Disposition');
				const filename = contDisp.split(';')[1].split('filename')[1].split('=')[1].trim();

				const blob = new Blob([response.body], { type: 'application/pdf' });
				const url = window.URL.createObjectURL(blob);

				const linkToFile = document.createElement('a');
				linkToFile.href = url;
				linkToFile.download = filename;
				linkToFile.click();
			})
			.finally(() => (this.loading = false));
	}

	public getBatchEditDetail(mailBatchId: number): Promise<MailBatchDetail> {
		return this.httpClient
			.get<MailBatchDetail>(
				this.environment.apiUrl + 'api/Contract/GetMailBatchEditDetail?mailBatchId=' + mailBatchId
			)
			.toPromise();
	}

	public updateMailBatchDetail(mailBatchDetail: MailBatchDetail): Observable<MailBatchDetail> {
		return this.httpClient.post<MailBatchDetail>(
			this.environment.apiUrl + 'api/Contract/UpdateMailBatchDetail',
			mailBatchDetail
		);
	}
}
